import Modal from "../../../../../ui/Modal/Modal";
import { Controller, useForm } from "react-hook-form";
import Label from "../../../../../ui/Label/Label";
import { useCallback } from "react";
import Button from "../../../../../ui/Button/Button";
import styles from "./PurchaseCreditsModal.module.scss";
import { graphql } from "../../../../../__generated__/gql";
import { useMutation } from "urql";
import { billingTierLabel } from "../../../../../util/billing";
import useUser from "../../../../../providers/UserProvider/useUser";
import Select from "../../../../../ui/Select/Select";
import Tabs from "../../../../../ui/Tabs/Tabs";

const CREDIT_AMOUNTS = [
  { value: "5", label: "5" },
  { value: "10", label: "10" },
  { value: "50", label: "50" },
  { value: "100", label: "100" },
  { value: "500", label: "500" },
  { value: "1000", label: "1000" },
  { value: "5000", label: "5000" },
];

const KEYWORD_CREDIT_AMOUNTS = [
  { value: "100", label: "100" },
  { value: "200", label: "200" },
  { value: "500", label: "500" },
  { value: "1000", label: "1000" },
  { value: "2000", label: "2000" },
  { value: "5000", label: "5000" },
];

const CreateCreditCheckoutMutation = graphql(/* GraphQL */ `
  mutation createCreditCheckout($input: CreateCreditCheckoutInput!) {
    createCreditCheckout(input: $input) {
      checkoutUrl
    }
  }
`);

const CreateKeywordCreditCheckoutMutation = graphql(/* GraphQL */ `
  mutation createKeywordCreditCheckout(
    $input: CreateKeywordCreditCheckoutInput!
  ) {
    createKeywordCreditCheckout(input: $input) {
      checkoutUrl
    }
  }
`);

type PurchaseCreditsForm = {
  credits: number;
};

function ArticleCredits({ onClose }: { onClose: () => void }) {
  const { billingInfo } = useUser();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isSubmitSuccessful },
    watch,
  } = useForm<PurchaseCreditsForm>({
    defaultValues: {
      credits: parseInt(CREDIT_AMOUNTS[0].value),
    },
  });
  const [, createCreditCheckout] = useMutation(CreateCreditCheckoutMutation);

  const creditPrice = billingInfo.pricePerArticle / 100;

  const credits = watch("credits");

  const onSubmit = useCallback(
    async (data: PurchaseCreditsForm) => {
      const checkoutSession = await createCreditCheckout({
        input: {
          credits: data.credits,
          returnUrl: window.location.href,
          referralCode:
            "tolt_referral" in window &&
            typeof window.tolt_referral === "string"
              ? window.tolt_referral
              : null,
        },
      });

      if (checkoutSession.data?.createCreditCheckout.checkoutUrl) {
        window.location.href =
          checkoutSession.data.createCreditCheckout.checkoutUrl;
      }
    },
    [createCreditCheckout],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.innerBody}>
      <Label label="Number of credits">
        <Controller
          control={control}
          name="credits"
          render={({ field }) => (
            <Select
              withinPortal={
                document.querySelector<HTMLElement>("body") ?? undefined
              }
              data={CREDIT_AMOUNTS}
              value={field.value?.toString()}
              onChange={(value) =>
                field.onChange(value ? parseInt(value) : null)
              }
            />
          )}
        />
      </Label>
      <div className={styles.pricePreview}>
        <div className={styles.planPrice}>
          <span>{`Article Credits (${
            billingTierLabel[billingInfo?.billingTier]
          } Plan)`}</span>
          <span>{`$${creditPrice}`}</span>
          <span>{`${credits} Credits`}</span>
        </div>
        <div className={styles.total}>
          <span>Total</span>
          <span>{`$${Intl.NumberFormat(undefined, {}).format(
            credits * creditPrice,
          )}`}</span>
        </div>
      </div>
      <div className={styles.buttonGroup}>
        <Button
          variant="outline"
          onClick={onClose}
          disabled={isSubmitting || isSubmitSuccessful}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={isSubmitting || isSubmitSuccessful}
          disabled={isSubmitting || isSubmitSuccessful}
        >
          Purchase
        </Button>
      </div>
    </form>
  );
}

function KeywordCredits({ onClose }: { onClose: () => void }) {
  const { billingInfo } = useUser();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isSubmitSuccessful },
    watch,
  } = useForm<PurchaseCreditsForm>({
    defaultValues: {
      credits: parseInt(KEYWORD_CREDIT_AMOUNTS[0].value),
    },
  });
  const [, createKeywordCreditCheckout] = useMutation(
    CreateKeywordCreditCheckoutMutation,
  );

  const creditPrice = billingInfo.keywordQueryCreditPrice / 100;

  const credits = watch("credits");

  const onSubmit = useCallback(
    async (data: PurchaseCreditsForm) => {
      const checkoutSession = await createKeywordCreditCheckout({
        input: {
          credits: data.credits,
          returnUrl: window.location.href,
          referralCode:
            "tolt_referral" in window &&
            typeof window.tolt_referral === "string"
              ? window.tolt_referral
              : null,
        },
      });

      if (checkoutSession.data?.createKeywordCreditCheckout.checkoutUrl) {
        window.location.href =
          checkoutSession.data.createKeywordCreditCheckout.checkoutUrl;
      }
    },
    [createKeywordCreditCheckout],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.innerBody}>
      <Label label="Number of credits">
        <Controller
          control={control}
          name="credits"
          render={({ field }) => (
            <Select
              withinPortal={
                document.querySelector<HTMLElement>("body") ?? undefined
              }
              data={KEYWORD_CREDIT_AMOUNTS}
              value={field.value?.toString()}
              onChange={(value) =>
                field.onChange(value ? parseInt(value) : null)
              }
            />
          )}
        />
      </Label>
      <div className={styles.pricePreview}>
        <div className={styles.planPrice}>
          <span>{`Keyword Credits (${
            billingTierLabel[billingInfo?.billingTier]
          } Plan)`}</span>
          <span>{`$${creditPrice}`}</span>
          <span>{`${credits} Credits`}</span>
        </div>
        <div className={styles.total}>
          <span>Total</span>
          <span>{`$${Intl.NumberFormat(undefined, {}).format(
            credits * creditPrice,
          )}`}</span>
        </div>
      </div>
      <div className={styles.buttonGroup}>
        <Button
          variant="outline"
          onClick={onClose}
          disabled={isSubmitting || isSubmitSuccessful}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={isSubmitting || isSubmitSuccessful}
          disabled={isSubmitting || isSubmitSuccessful}
        >
          Purchase
        </Button>
      </div>
    </form>
  );
}

function PurchaseCreditsModal({
  isOpen,
  onClose,
  defaultValue = "articles",
}: {
  isOpen: boolean;
  onClose: () => void;
  defaultValue?: "articles" | "keywords";
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Purchase extra credits"
      classNames={{ body: styles.modalBody }}
    >
      <Tabs
        classNames={{ list: styles.tabs }}
        defaultValue={defaultValue}
        tabs={[
          {
            id: "articles",
            label: "Article credits",
            content: <ArticleCredits onClose={onClose} />,
          },
          {
            id: "keywords",
            label: "Keyword credits",
            content: <KeywordCredits onClose={onClose} />,
          },
        ]}
      />
    </Modal>
  );
}

export default PurchaseCreditsModal;
